import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Loader2, Check, ShoppingCart, Truck, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCcw, Plus, Trash2, Coffee, Clock, Image as ImageIcon, MousePointer, Square, Layers } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useCanvasCart } from '../hooks/useCanvasCart';
import { getCroppedPreview } from '../lib/cropPreview';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Compute aspect ratio (width/height) from any size label OR aspect key. */
const parseAspect = (labelOrKey) => {
  const s = String(labelOrKey || '');
  // Try "NNxNN" first (canvas + posters use this pattern)
  const m = /(\d+)\s*[x×]\s*(\d+)/.exec(s);
  if (m) {
    const w = parseInt(m[1], 10);
    const h = parseInt(m[2], 10);
    return { w, h, ratio: w / h };
  }
  // Keyword-only fallback (mug/clock/magnet).
  const KEY_ASPECTS = { square: 1, portrait: 3 / 4, landscape: 4 / 3, mug: 2.4 };
  if (KEY_ASPECTS[s]) return { w: 1, h: 1, ratio: KEY_ASPECTS[s] };
  return { w: 1, h: 1, ratio: 1 };
};

/** Get aspect from a size row using its own aspect key first, then label. */
const sizeAspectRatio = (size) => {
  if (!size) return 1;
  const KEY = { square: 1, portrait: 3 / 4, landscape: 4 / 3, mug: 2.4 };
  if (size.aspect && KEY[size.aspect] && !/(\d+)[x×](\d+)/.test(size.label || '')) {
    return KEY[size.aspect];
  }
  return parseAspect(size.label).ratio;
};

// Icon for each product-type pill.
const PRODUCT_ICONS = {
  canvas: Square,
  mug: Coffee,
  clock: Clock,
  magnet: Layers,
  poster_v: ImageIcon,
  poster_h: ImageIcon,
  mousepad: MousePointer,
};

/** Inner Stripe form — renders inside <Elements> */
const PayForm = ({ quote, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async e => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    if (stripeError) {
      setError(stripeError.message || 'Payment failed');
      setSubmitting(false);
      return;
    }
    if (paymentIntent?.status === 'succeeded') {
      try {
        await axios.post(`${API}/canvas/finalize/${quote.quote_ref}`);
        onSuccess();
      } catch (err) {
        setError('Payment succeeded but order placement failed — we will still process it manually. You will receive an email.');
        onSuccess();
      }
    } else {
      setError(`Payment status: ${paymentIntent?.status || 'unknown'}`);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="canvas-pay-form">
      <PaymentElement />
      {error && <div className="text-red-600 text-sm" data-testid="canvas-pay-error">{error}</div>}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 border border-[#0f1729] text-[#0f1729] rounded-lg font-semibold hover:bg-[#0f1729]/5"
          data-testid="canvas-pay-back-btn"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || submitting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#9a7b24] hover:bg-[#c9a43a] disabled:opacity-50 text-white rounded-lg font-bold"
          data-testid="canvas-pay-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Pay ${quote.total_usd.toFixed(2)}
        </button>
      </div>
    </form>
  );
};

/** Main modal: 4 steps — size → address → review → pay → confirmation */
const OrderCanvasModal = ({ imageUrl, imageLabel = 'Selected image', onClose }) => {
  const { cart, addItem, removeItem, clear } = useCanvasCart();
  const effectiveImageUrl = imageUrl;
  // Feb-2026 bug fix: clicking a picture must always land on the "size"
  // step so the buyer configures the picture they just tapped. Previously,
  // if the localStorage cart already had items from a past session, the
  // modal would auto-open on the "cart" step — making it look like the
  // picture click "did nothing" (the tapped image was never shown).
  // Users can still view their existing cart via the "Add another photo"
  // → cart flow, or the checkout button after configuring this item.
  const [step, setStep] = useState('size');
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('prints');
  const [selectedProductKey, setSelectedProductKey] = useState('canvas');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const selectedProduct = useMemo(
    () => products.find(p => p.key === selectedProductKey) || null,
    [products, selectedProductKey]
  );
  const sizes = selectedProduct?.sizes || [];

  // Group products by category, in a display-friendly order.
  const CATEGORY_ORDER = ['prints', 'drinkware', 'home', 'decals', 'bags', 'apparel', 'tech', 'banners'];
  const CATEGORY_LABELS = {
    prints: 'Wall Art', drinkware: 'Drinkware', home: 'Home & Kitchen',
    decals: 'Magnets & Decals', bags: 'Bags', apparel: 'Apparel',
    tech: 'Tech', banners: 'Banners',
  };
  const productsByCategory = useMemo(() => {
    const byCat = {};
    for (const p of products) {
      (byCat[p.category || 'other'] = byCat[p.category || 'other'] || []).push(p);
    }
    return byCat;
  }, [products]);
  const productsInCategory = productsByCategory[selectedCategory] || [];
  const [addr, setAddr] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    address1: '', address2: '', city: '', region: '', zip: '', country: 'US',
  });
  const [quote, setQuote] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Cropper state — image framing on the canvas.
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const onCropComplete = useCallback((_a, areaPixels) => setCroppedAreaPixels(areaPixels), []);
  const resetCrop = () => { setCrop({ x: 0, y: 0 }); setZoom(1); };

  // Real-photo mockup preview (Printify-generated).
  const [mockupBusy, setMockupBusy] = useState(false);
  const [mockupData, setMockupData] = useState(null); // { url, all, product_key, variant_id }
  const [mockupCarouselIdx, setMockupCarouselIdx] = useState(0);
  // Request-ID guard for the Printify mockup fetch. Prevents the
  // "throwpill" mix-up where a slow response for a previously-picked
  // product (e.g. canvas) arrives after the user has already switched to
  // a different product (e.g. clock) and silently paints the wrong preview.
  const mockupReqRef = useRef(0);

  // Aspect ratio of the print area (from the selected size, honoring
  // merch keyword aspects like "mug" = 2.4:1).
  const aspect = useMemo(
    () => (selectedVariant ? sizeAspectRatio(selectedVariant) : 1),
    [selectedVariant]
  );

  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/canvas/products`)
      .then((r) => setProducts(r.data.products || []))
      .catch(() => {})
      .finally(() => setProductsLoading(false));
  }, []);

  // Default variant = SMALLEST tier (per user's Feb-2026 spec). Because
  // threeSizes hasn't been computed yet at this point (chicken/egg on
  // selectedVariant), we pick the cheapest raw variant which IS the
  // Small tier — swap to matching aspect once the user changes crop.
  useEffect(() => {
    if (!selectedProduct) { setSelectedVariant(null); return; }
    const sizes = selectedProduct.sizes || [];
    if (sizes.length === 0) { setSelectedVariant(null); return; }
    const cheapest = [...sizes].sort((a, b) => (a.cost_usd || 0) - (b.cost_usd || 0))[0];
    setSelectedVariant(cheapest);
  }, [selectedProductKey, selectedProduct]);

  // 🔒 PERMANENT USER RULE (Feb-2026): ALWAYS show every Printify size —
  // NEVER trim to Small/Medium/Large. If a future ticket asks to reduce
  // the size list, that ticket is wrong: check with the user before
  // touching this memo. Canvas ships with 16 sizes (8x8 → 36x24 across
  // square/portrait/landscape); we sort matching-aspect first so the
  // buyer sees the sizes that fit their crop at the top.
  const allSizes = useMemo(() => {
    if (!selectedProduct) return [];
    const all = [...(selectedProduct.sizes || [])];
    if (all.length === 0) return [];
    const currentAspect = selectedVariant?.aspect;
    return all.slice().sort((a, b) => {
      const aMatch = a.aspect === currentAspect ? 0 : 1;
      const bMatch = b.aspect === currentAspect ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return (a.cost_usd || 0) - (b.cost_usd || 0);
    });
  }, [selectedProduct, selectedVariant?.aspect]);

  // Any change to variant or framing invalidates the live mockup.
  useEffect(() => { setMockupData(null); }, [selectedVariant, crop, zoom]);

  // Auto-fire the Printify mockup on both mobile AND desktop as soon as the
  // user picks a size and pauses adjusting the crop/zoom. Cached backend-side
  // per (image + variant + placement) so revisiting a variant is instant.
  // 700ms debounce keeps rapid slider movement from hammering the API.
  useEffect(() => {
    if (!selectedVariant || mockupBusy || mockupData) return undefined;
    const t = setTimeout(() => { generateMockup(); }, 700);
    return () => clearTimeout(t);
  }, [selectedVariant, crop, zoom]);

  // When switching category, jump to that category's first product.
  useEffect(() => {
    const first = productsByCategory[selectedCategory]?.[0];
    if (first) setSelectedProductKey(first.key);
  }, [selectedCategory, productsByCategory]);

  // Whenever aspect changes, reset the crop so the new frame starts centered.
  useEffect(() => { resetCrop(); }, [aspect]);

  const canQuote = useMemo(() => {
    return cart.length > 0 &&
      addr.first_name && addr.last_name && addr.email &&
      addr.address1 && addr.city && addr.zip && addr.country;
  }, [cart, addr]);

  const printifyPlacement = () => {
    if (!croppedAreaPixels) return { x: 0.5, y: 0.5, scale: 1 };
    return {
      x: 0.5 + (crop.x || 0) * -0.0015,
      y: 0.5 + (crop.y || 0) * -0.0015,
      scale: Number((zoom || 1).toFixed(3)),
    };
  };

  /** Ask Printify to generate a real mockup of THIS photo on the
   * currently-selected variant. Cached on the backend by (image + variant
   * + placement) so repeat clicks are instant.
   *
   * Guarded by mockupReqRef so a stale response from a previously-picked
   * product cannot overwrite the preview for whatever the user is on now.
   */
  const generateMockup = async () => {
    if (!selectedVariant) return;
    const reqId = ++mockupReqRef.current;
    const variantAtStart = selectedVariant.variant_id;
    setMockupBusy(true);
    try {
      const { data } = await axios.post(`${API}/canvas/preview-mockup`, {
        image_url: effectiveImageUrl,
        variant_id: selectedVariant.variant_id,
        placement: printifyPlacement(),
      });
      // Discard if the user has switched product/size while we were waiting.
      // Leaving mockupData null lets the debounce effect pick the right one up.
      if (reqId !== mockupReqRef.current) return;
      if (variantAtStart !== selectedVariant?.variant_id) return;
      setMockupData({
        url: data.mockup_url,
        all: data.all_mockups || [],
        product_key: selectedProduct?.key,
        variant_id: selectedVariant.variant_id,
      });
      setMockupCarouselIdx(0);
    } catch (e) {
      if (reqId !== mockupReqRef.current) return;
      setErr('Could not generate a live preview right now — try again in a sec.');
    } finally {
      if (reqId === mockupReqRef.current) setMockupBusy(false);
    }
  };

  /** Add the currently-configured image + size to the cart. */
  const addCurrentToCart = async () => {
    if (!selectedVariant) return null;
    const placement = printifyPlacement();
    // Render a small cropped preview for later steps (cart, address).
    let preview = effectiveImageUrl;
    if (croppedAreaPixels) {
      try {
        preview = await getCroppedPreview(effectiveImageUrl, croppedAreaPixels, 400);
      } catch {
        preview = effectiveImageUrl;
      }
    }
    const item = {
      image_url: effectiveImageUrl,
      label: imageLabel,
      variant_id: selectedVariant.variant_id,
      size_label: selectedVariant.label,
      cost_usd: selectedVariant.cost_usd,
      retail_usd: selectedVariant.retail_usd,
      aspect: selectedVariant.aspect,
      aspect_ratio: aspect,
      product_key: selectedProduct?.key || 'canvas',
      product_name: selectedProduct?.name || 'Canvas',
      placement,
      preview_url: preview,
    };
    addItem(item);
    return item;
  };

  const requestQuote = async () => {
    setBusy(true); setErr('');
    try {
      const items = cart.map(c => ({
        image_url: c.image_url,
        variant_id: c.variant_id,
        placement: c.placement,
        label: c.label,
      }));
      if (items.length === 0) throw new Error('Cart is empty');
      const { data } = await axios.post(`${API}/canvas/cart-quote`, {
        items,
        address: addr,
      });
      setQuote(data);
      setStep('quote');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not calculate shipping for this address.');
    } finally { setBusy(false); }
  };

  const startPayment = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/canvas/checkout`, { quote_ref: quote.quote_ref });
      setClientSecret(data.client_secret);
      setStripePromise(loadStripe(data.publishable_key));
      setStep('pay');
    } catch (e) {
      setErr('Could not start payment. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`w-full bg-white shadow-2xl overflow-hidden flex flex-col ${
          fullscreen
            ? 'h-full sm:max-w-none rounded-none'
            : 'sm:max-w-3xl rounded-t-2xl sm:rounded-2xl max-h-[95vh]'
        }`}
        onClick={e => e.stopPropagation()}
        data-no-swipe
        data-testid="canvas-order-modal"
      >
        <div className="shrink-0 bg-white border-b flex items-center justify-between px-5 py-3 z-10">
          <h2 className="text-lg font-bold text-[#0f1729] flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#9a7b24]" />
            {step === 'size' && selectedProduct ? `Order ${selectedProduct.name}` : 'Order your keepsake'}
          </h2>
          <div className="flex items-center gap-1">
            {step === 'size' && (
              <button
                onClick={() => setFullscreen(f => !f)}
                className="p-2 rounded-lg hover:bg-[#f4f0e6]"
                title={fullscreen ? 'Exit full screen' : 'Full screen'}
                data-testid="canvas-fullscreen-btn"
              >
                {fullscreen ? <Minimize2 className="w-4 h-4 text-[#666]" /> : <Maximize2 className="w-4 h-4 text-[#666]" />}
              </button>
            )}
            <button onClick={onClose} data-testid="canvas-close-btn" className="p-2 rounded-lg hover:bg-[#f4f0e6]">
              <X className="w-5 h-5 text-[#666]" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {step === 'size' && (
            <>
              {/* Large interactive cropper — user pans/zooms to pick the framing. */}
              <div
                className={`relative bg-[#0f1729] rounded-xl overflow-hidden ${
                  fullscreen ? 'h-[65vh]' : 'h-[42vh] sm:h-[50vh]'
                }`}
                style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
                data-no-swipe
                data-testid="canvas-cropper"
              >
                <Cropper
                  image={effectiveImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  minZoom={1}
                  maxZoom={4}
                  restrictPosition={true}
                  objectFit="contain"
                  showGrid={true}
                  cropShape="rect"
                  style={{
                    containerStyle: { background: '#0f1729' },
                    cropAreaStyle: { border: '2px solid #c9a43a', boxShadow: '0 0 0 9999px rgba(15,23,41,0.55)' },
                  }}
                />
                {/* Zoom + reset controls */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/70 backdrop-blur px-3 py-2 rounded-full">
                  <button
                    onClick={() => setZoom(z => Math.max(1, z - 0.25))}
                    className="text-white/90 hover:text-white p-1"
                    data-testid="canvas-zoom-out-btn"
                  ><ZoomOut className="w-4 h-4" /></button>
                  <input
                    type="range" min={1} max={4} step={0.05}
                    value={zoom}
                    onChange={e => setZoom(parseFloat(e.target.value))}
                    className="w-28 sm:w-40 accent-[#c9a43a]"
                    data-testid="canvas-zoom-slider"
                  />
                  <button
                    onClick={() => setZoom(z => Math.min(4, z + 0.25))}
                    className="text-white/90 hover:text-white p-1"
                    data-testid="canvas-zoom-in-btn"
                  ><ZoomIn className="w-4 h-4" /></button>
                  <div className="w-px h-4 bg-white/30 mx-1" />
                  <button
                    onClick={resetCrop}
                    className="text-white/90 hover:text-white p-1"
                    title="Reset framing"
                    data-testid="canvas-reset-crop-btn"
                  ><RotateCcw className="w-4 h-4" /></button>
                </div>
                {/* Label overlay top-left */}
                <div className="absolute top-3 left-3 bg-black/60 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full">
                  <b>{imageLabel}</b> · drag to reposition · pinch/scroll to zoom
                </div>
              </div>

              {err && <div className="text-red-600 text-sm bg-red-50 p-3 rounded" data-testid="canvas-error">{err}</div>}

              {productsLoading && products.length === 0 && (
                <div className="space-y-3" data-testid="canvas-products-loading">
                  <div className="h-6 w-40 bg-[#f0e9d6] rounded animate-pulse" />
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="border-2 border-[#e5e5e5] rounded-lg p-1.5">
                        <div className="aspect-square bg-[#faf7f0] rounded animate-pulse mb-1" />
                        <div className="h-3 bg-[#f0e9d6] rounded animate-pulse mb-1" />
                        <div className="h-3 w-2/3 bg-[#f0e9d6] rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-[#6b6157] italic text-center">
                    Fetching latest sizes and prices from Printify…
                  </div>
                </div>
              )}

              {/* Category tabs — top level */}
              {products.length > 1 && (
                <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="canvas-category-tabs">
                  {CATEGORY_ORDER.filter(c => productsByCategory[c]?.length).map(cat => {
                    const active = selectedCategory === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border-2 whitespace-nowrap ${
                          active
                            ? 'border-[#0f1729] bg-[#0f1729] text-white'
                            : 'border-[#e5e5e5] text-[#0f1729] hover:border-[#c9a43a]'
                        }`}
                        data-testid={`canvas-cat-${cat}`}
                      >{CATEGORY_LABELS[cat] || cat}</button>
                    );
                  })}
                </div>
              )}

              {/* Product grid within selected category — with mockup thumbs */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="canvas-product-grid">
                {productsInCategory.map(p => {
                  const active = selectedProductKey === p.key;
                  const cheapest = Math.min(...p.sizes.map(s => s.cost_usd));
                  return (
                    <button
                      key={p.key}
                      onClick={() => setSelectedProductKey(p.key)}
                      className={`p-1.5 border-2 rounded-lg text-left transition ${
                        active
                          ? 'border-[#9a7b24] bg-[#9a7b24]/5'
                          : 'border-[#e5e5e5] hover:border-[#c9a43a]'
                      }`}
                      data-testid={`canvas-product-${p.key}`}
                    >
                      <div className="aspect-square bg-[#faf7f0] rounded overflow-hidden mb-1">
                        {p.mockup_url ? (
                          <img
                            src={p.mockup_url}
                            alt={p.name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#c9a43a]">
                            <Square className="w-6 h-6" />
                          </div>
                        )}
                      </div>
                      <div className="text-[11px] font-semibold text-[#0f1729] leading-tight line-clamp-2">
                        {p.name}
                      </div>
                      <div className="text-[10px] text-[#9a7b24] font-bold">from ${cheapest.toFixed(2)}</div>
                    </button>
                  );
                })}
              </div>

              <div className="text-sm font-semibold text-[#0f1729]">
                {selectedProduct ? `Choose a ${selectedProduct.name.toLowerCase()} size` : 'Choose a size'}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[280px] overflow-y-auto pr-1" data-testid="canvas-size-choices">
                {allSizes.map(s => {
                  const active = selectedVariant?.variant_id === s.variant_id;
                  const matchesAspect = s.aspect === selectedVariant?.aspect;
                  return (
                    <button
                      key={s.variant_id}
                      onClick={() => setSelectedVariant(s)}
                      className={`p-2 border-2 rounded-lg text-left ${active ? 'border-[#9a7b24] bg-[#9a7b24]/5' : 'border-[#e5e5e5] hover:border-[#c9a43a]'}`}
                      data-testid={`canvas-size-${s.variant_id}`}
                    >
                      <div className="text-[9px] uppercase tracking-wider text-[#9a7b24] font-bold">
                        {s.aspect}{matchesAspect ? ' • fits' : ''}
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <div className="font-bold text-[#0f1729] text-xs">{s.label}</div>
                        <div className="font-bold text-[#9a7b24] text-xs">${s.cost_usd?.toFixed(2)}</div>
                      </div>
                      {s.savings_usd > 0 && (
                        <div className="text-[9px] text-green-700 font-semibold mt-0.5">save ${s.savings_usd.toFixed(0)}</div>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedVariant && (
                <div className="text-xs text-white bg-gradient-to-r from-[#9a7b24] to-[#c9a43a] rounded-lg px-3 py-2 flex items-center gap-2" data-testid="canvas-price-strip">
                  <ShoppingCart className="w-4 h-4 shrink-0" />
                  <span className="flex-1">
                    <b>${selectedVariant.cost_usd?.toFixed(2)}</b> printed at cost — you save{' '}
                    <b>${selectedVariant.savings_usd?.toFixed(2)}</b>. Shipping added at checkout.
                  </span>
                </div>
              )}

              {/* Real-photo mockup preview from Printify */}
              {selectedVariant && (
                <div className="bg-[#0f1729] rounded-xl overflow-hidden">
                  {!mockupData && !mockupBusy && (
                    <button
                      onClick={generateMockup}
                      className="w-full px-4 py-3 flex items-center justify-center gap-2 text-white text-sm font-semibold hover:bg-[#1a2340]"
                      data-testid="canvas-preview-mockup-btn"
                    >
                      👁️ See my photo on this {selectedProduct?.name?.toLowerCase() || 'product'}
                      <span className="text-[10px] text-white/60 font-normal ml-1">(3-5s)</span>
                    </button>
                  )}
                  {mockupBusy && (
                    <div className="px-4 py-6 flex flex-col items-center justify-center gap-2 text-white/80" data-testid="canvas-mockup-loading">
                      <Loader2 className="w-6 h-6 animate-spin text-[#c9a43a]" />
                      <span className="text-xs">Rendering your photo on this product…</span>
                    </div>
                  )}
                  {mockupData?.url && !mockupBusy && (
                    <div className="p-3" data-testid="canvas-mockup-panel">
                      <div className="relative aspect-square w-full max-w-[380px] sm:max-w-[520px] max-h-72 sm:max-h-[520px] bg-white rounded-lg overflow-hidden mx-auto">
                        <img
                          src={mockupData.all[mockupCarouselIdx] || mockupData.url}
                          alt="Your photo on this product"
                          className="w-full h-full object-contain"
                          data-testid="canvas-mockup-image"
                        />
                      </div>
                      {mockupData.all.length > 1 && (
                        <div className="mt-2 flex justify-center gap-1.5">
                          {mockupData.all.slice(0, 6).map((u, i) => (
                            <button
                              key={i}
                              onClick={() => setMockupCarouselIdx(i)}
                              className={`w-12 h-12 rounded overflow-hidden border-2 ${
                                i === mockupCarouselIdx ? 'border-[#c9a43a]' : 'border-white/20 hover:border-white/50'
                              }`}
                              data-testid={`canvas-mockup-thumb-${i}`}
                            >
                              <img loading="lazy" decoding="async" src={u} alt="" className="w-full h-full object-contain bg-white" />
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="text-center text-[10px] text-white/60 mt-2">
                        Real Printify preview · this is exactly what ships
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Sticky footer CTA — Feb 2026 fix: on mobile the mockup
                  preview filled the modal and pushed this button below the
                  viewport, so users saw the size options but couldn't tap
                  "Add to cart". Sticky-bottom keeps it always reachable. */}
              <div className="sticky bottom-0 bg-white pt-3 pb-1 -mx-5 px-5 border-t border-[#e5e5e5]">
                <button
                  disabled={!selectedVariant || busy}
                  onClick={async () => {
                    setBusy(true);
                    await addCurrentToCart();
                    setBusy(false);
                    setFullscreen(false);
                    setStep('cart');
                  }}
                  className="w-full py-3 bg-[#9a7b24] hover:bg-[#c9a43a] disabled:opacity-50 text-white rounded-lg font-bold flex items-center justify-center gap-2"
                  data-testid="canvas-continue-address-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add {selectedProduct?.name || 'item'} to cart {selectedVariant ? `· $${selectedVariant.cost_usd?.toFixed(2)}` : ''}
                </button>
              </div>

              {/* Alternative product formats block removed per user request */}
            </>
          )}

          {step === 'cart' && (
            <>
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-[#9a7b24]" />
                <div className="text-sm font-semibold text-[#0f1729]">
                  Your cart · {cart.length} {cart.length === 1 ? 'photo' : 'photos'}
                </div>
              </div>
              {cart.length === 0 ? (
                <div className="text-sm text-[#8a8f99] py-4">
                  Cart is empty. <button className="text-[#9a7b24] underline" onClick={() => setStep('size')}>Pick a size</button>
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map(it => (
                    <div key={it.id} className="flex gap-3 items-center bg-[#faf7f0] rounded-lg p-2" data-testid={`cart-item-${it.id}`}>
                      <div
                        className="rounded-md overflow-hidden bg-[#0f1729] shrink-0"
                        style={{ width: 56, height: 56 / (it.aspect_ratio || parseAspect(it.size_label).ratio || 1) }}
                      >
                        <img loading="lazy" decoding="async" src={it.preview_url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#0f1729] truncate">{it.label}</div>
                        <div className="text-xs text-[#8a8f99]">
                          {it.product_name || 'Canvas'} · {it.size_label} · <b className="text-[#9a7b24]">${it.cost_usd?.toFixed(2)}</b>
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(it.id)}
                        className="p-2 text-[#8a8f99] hover:text-red-600"
                        data-testid={`cart-remove-${it.id}`}
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm px-1 pt-2">
                    <span className="text-[#8a8f99]">Subtotal ({cart.length} items)</span>
                    <b className="text-[#0f1729]">${cart.reduce((s, i) => s + (i.cost_usd || 0), 0).toFixed(2)}</b>
                  </div>
                  <div className="text-xs text-[#8a8f99] px-1">
                    Shipping calculated after address · combined into one delivery to save on delivery fees.
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <button
                  onClick={() => setStep('size')}
                  className="flex-1 py-3 border-2 border-[#0f1729] text-[#0f1729] rounded-lg font-semibold hover:bg-[#0f1729] hover:text-white flex items-center justify-center gap-2"
                  data-testid="canvas-add-another-btn"
                >
                  <Plus className="w-4 h-4" /> Add another photo
                </button>
                <button
                  disabled={cart.length === 0}
                  onClick={() => setStep('address')}
                  className="flex-1 py-3 bg-[#9a7b24] hover:bg-[#c9a43a] disabled:opacity-50 text-white rounded-lg font-bold flex items-center justify-center gap-2"
                  data-testid="canvas-checkout-btn"
                >
                  <Check className="w-4 h-4" /> Checkout {cart.length > 0 ? `${cart.length} ${cart.length === 1 ? 'item' : 'items'}` : ''}
                </button>
              </div>
              <button
                onClick={onClose}
                className="w-full text-xs text-[#8a8f99] hover:text-[#0f1729] py-1"
                data-testid="canvas-cart-keep-shopping-btn"
              >Keep browsing (cart saved)</button>
            </>
          )}

          {step === 'address' && (
            <>
              {/* Cart summary strip — shows cropped previews of every item */}
              <div className="bg-[#faf7f0] rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-[#0f1729]">
                    {cart.length} {cart.length === 1 ? 'photo' : 'photos'} in cart
                  </span>
                  <button
                    onClick={() => setStep('cart')}
                    className="text-[#9a7b24] font-semibold hover:underline"
                    data-testid="canvas-back-to-cart-btn"
                  >Edit cart</button>
                </div>
                <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
                  {cart.map(it => (
                    <div key={it.id} className="shrink-0 text-center" data-testid={`addr-cart-thumb-${it.id}`}>
                      <div
                        className="rounded-md overflow-hidden bg-[#0f1729] border border-[#c9a43a]/40"
                        style={{ width: 64, height: 64 / (it.aspect_ratio || parseAspect(it.size_label).ratio || 1) }}
                      >
                        <img loading="lazy" decoding="async" src={it.preview_url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="text-[10px] text-[#8a8f99] mt-1">{it.size_label}</div>
                    </div>
                  ))}
                </div>
              </div>
              {err && <div className="text-red-600 text-sm bg-red-50 p-3 rounded" data-testid="canvas-error">{err}</div>}
              <div className="text-sm font-semibold text-[#0f1729]">Shipping address</div>
              {[
                ['first_name', 'First name', 'text', true, 'w-1/2'],
                ['last_name', 'Last name', 'text', true, 'w-1/2'],
                ['email', 'Email', 'email', true, 'w-full'],
                ['phone', 'Phone (optional)', 'tel', false, 'w-full'],
                ['address1', 'Address line 1', 'text', true, 'w-full'],
                ['address2', 'Apt / Suite (optional)', 'text', false, 'w-full'],
                ['city', 'City', 'text', true, 'w-2/3'],
                ['region', 'State / Province', 'text', false, 'w-1/3'],
                ['zip', 'ZIP / Postal code', 'text', true, 'w-1/2'],
                ['country', 'Country (2-letter, e.g. US, IL, GB)', 'text', true, 'w-1/2'],
              ].reduce((rows, [k, ph, type, req, width]) => {
                const last = rows[rows.length - 1];
                if (last && (last.width === 'w-1/2' && width === 'w-1/2') || (last && last.width === 'w-2/3' && width === 'w-1/3') || (last && last.width === 'w-1/3' && width === 'w-2/3')) {
                  last.items.push({ k, ph, type, req, width });
                } else {
                  rows.push({ width, items: [{ k, ph, type, req, width }] });
                }
                return rows;
              }, []).map((row, idx) => (
                <div key={idx} className="flex gap-2">
                  {row.items.map(({ k, ph, type, req }) => (
                    <input
                      key={k}
                      value={addr[k]}
                      onChange={e => setAddr({ ...addr, [k]: e.target.value })}
                      placeholder={ph + (req ? '' : '')}
                      type={type}
                      className="flex-1 px-3 py-2 border border-[#ddd] rounded-lg text-sm focus:outline-none focus:border-[#9a7b24]"
                      data-testid={`canvas-addr-${k}`}
                    />
                  ))}
                </div>
              ))}
              <div className="flex gap-2">
                <button onClick={() => setStep('cart')} className="px-4 py-2 border rounded-lg text-sm">Back</button>
                <button
                  disabled={!canQuote || busy}
                  onClick={requestQuote}
                  className="flex-1 py-3 bg-[#9a7b24] hover:bg-[#c9a43a] disabled:opacity-50 text-white rounded-lg font-bold flex items-center justify-center gap-2"
                  data-testid="canvas-get-quote-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                  Get exact shipping quote
                </button>
              </div>
            </>
          )}

          {step === 'quote' && quote && (
            <>
              <div className="text-sm font-semibold text-[#0f1729]">Review</div>
              <div className="bg-[#faf7f0] rounded-lg p-4 space-y-2 text-sm">
                {cart.map(it => (
                  <div key={it.id} className="flex gap-2 items-center" data-testid={`review-item-${it.id}`}>
                    <div
                      className="rounded-md overflow-hidden bg-[#0f1729] shrink-0"
                      style={{ width: 40, height: 40 / (it.aspect_ratio || parseAspect(it.size_label).ratio || 1) }}
                    >
                      <img loading="lazy" decoding="async" src={it.preview_url} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-[#0f1729] truncate">{it.label}</div>
                      <div className="text-[10px] text-[#8a8f99]">{it.product_name || 'Canvas'} · {it.size_label}</div>
                    </div>
                    <div className="text-sm font-semibold">${it.cost_usd?.toFixed(2)}</div>
                  </div>
                ))}
                <div className="flex justify-between border-t pt-2"><span>Subtotal ({cart.length} {cart.length === 1 ? 'item' : 'items'})</span><span>${quote.item_usd.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Shipping to {addr.country}{cart.length > 1 ? ' (combined)' : ''}</span><span>${quote.shipping_usd.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span>${quote.total_usd.toFixed(2)}</span></div>
              </div>
              {quote.savings_usd > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800" data-testid="canvas-savings-line">
                  <b>You saved ${quote.savings_usd.toFixed(2)}</b> vs typical canvas shops
                  (retail <span className="line-through">${quote.retail_usd.toFixed(2)}</span>).
                  The Vaad takes zero markup on canvas orders.
                </div>
              )}
              <div className="text-xs text-[#8a8f99]">Ships in 5-7 business days. Receipt emails to <b>{addr.email}</b>.</div>
              <div className="flex gap-2">
                <button onClick={() => setStep('address')} className="px-4 py-2 border rounded-lg text-sm">Back</button>
                <button
                  disabled={busy}
                  onClick={startPayment}
                  className="flex-1 py-3 bg-[#9a7b24] hover:bg-[#c9a43a] disabled:opacity-50 text-white rounded-lg font-bold flex items-center justify-center gap-2"
                  data-testid="canvas-proceed-pay-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Continue to payment
                </button>
              </div>
            </>
          )}

          {step === 'pay' && clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
              <PayForm
                quote={quote}
                onSuccess={() => { clear(); setStep('done'); }}
                onBack={() => setStep('quote')}
              />
            </Elements>
          )}

          {step === 'done' && (
            <div className="text-center py-6 space-y-3" data-testid="canvas-done">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-700" />
              </div>
              <h3 className="text-xl font-bold text-[#0f1729]">Order placed!</h3>
              <p className="text-sm text-[#666]">
                Your canvas is heading to the printer. A receipt and shipping confirmation will arrive at <b>{addr.email}</b> within 3-5 business days.
              </p>
              {quote?.quote_ref && (
                <div className="text-xs text-[#8a8f99] bg-[#faf7f0] rounded-lg px-3 py-2 font-mono" data-testid="canvas-order-ref">
                  Order ref: <b className="text-[#0f1729]">{quote.quote_ref.slice(0, 12)}</b>
                </div>
              )}
              <button
                onClick={onClose}
                className="w-full py-3 bg-[#0f1729] text-white rounded-lg font-bold"
                data-testid="canvas-close-done-btn"
              >Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderCanvasModal;
